import { useQuery } from '@tanstack/react-query';
import { BuildingIcon, Calendar, CheckCircle, Mail, MapPin, Shield, User as UserIcon, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import useLocale from '../contexts/LocaleContext';
import { getCurrentUser } from '../hooks/useAuth';
import { usersApi } from '../lib/api-client';
import { cn } from '../lib/utils';

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: string | number;
  bgColor?: string;
  iconColor?: string;
}

function StatCard({ icon: Icon, label, value, bgColor = 'bg-secondary', iconColor = 'text-primary' }: StatCardProps) {
  return (
    <div className='bg-card border border-border rounded-lg p-3 sm:p-5 transition-colors hover:bg-accent/5'>
      <div className='flex items-center gap-3 sm:gap-4'>
        <div className={cn('p-2 sm:p-2.5 rounded-lg', bgColor)}>
          <Icon className={cn('h-4 w-4 sm:h-5 sm:w-5', iconColor)} />
        </div>
        <div className='flex-1 min-w-0'>
          <p className='text-xs sm:text-sm text-muted-foreground'>{label}</p>
          <p className='text-base sm:text-lg font-medium text-foreground mt-1 truncate'>{value}</p>
        </div>
      </div>
    </div>
  );
}

export default function Profile() {
  const { dictionary } = useLocale();
  const user = getCurrentUser();
  const [formattedDate, setFormattedDate] = useState('');

  // Fetch user's location hierarchy
  const { data: locationHierarchy, isLoading: locationLoading } = useQuery({
    queryKey: ['user-location-hierarchy'],
    queryFn: () => usersApi.getMyLocationHierarchy(),
    enabled: !!user,
  });

  // Fetch assigned locations
  const { data: assignedLocations, isLoading: locationsLoading } = useQuery({
    queryKey: ['assigned-locations', user?.id],
    queryFn: () => usersApi.getAssignedLocations(user?.id || ''),
    enabled: !!user?.id,
  });

  // Format the created date
  useEffect(() => {
    if (user?.createdAt) {
      const date = new Date(user.createdAt);
      setFormattedDate(
        date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      );
    }
  }, [user]);

  // Format role for display
  const formatRole = (role: string) => {
    return role
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Get role badge color
  const getRoleBadgeColor = (role: string) => {
    const roleColors: Record<string, { bg: string; text: string }> = {
      ADMIN: { bg: 'bg-purple-100', text: 'text-purple-700' },
      PROVINCE_ADMIN: { bg: 'bg-secondary', text: 'text-primary' },
      DISTRICT_ADMIN: { bg: 'bg-green-100', text: 'text-green-700' },
      SECTOR_ADMIN: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      CELL_ADMIN: { bg: 'bg-orange-100', text: 'text-orange-700' },
      VILLAGE_ADMIN: { bg: 'bg-pink-100', text: 'text-pink-700' },
    };
    return roleColors[role] || { bg: 'bg-gray-100', text: 'text-gray-700' };
  };

  const roleColors = getRoleBadgeColor(user?.assignedRole?.code ?? user?.role ?? '');

  // Format location hierarchy
  const formatLocationHierarchy = () => {
    if (!locationHierarchy) return 'N/A';

    const locations = [
      locationHierarchy.provinceName,
      locationHierarchy.districtName,
      locationHierarchy.sectorName,
      locationHierarchy.cellName,
      locationHierarchy.villageName,
    ].filter(Boolean);

    return locations.join(' › ') || dictionary['profile.na'];
  };

  if (!user) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <p className='text-muted-foreground'>{dictionary['profile.no_user_data']}</p>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-background'>
      <div className='max-w-6xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-8'>
        {/* Header */}
        <div className='mb-3 sm:mb-4'>
          <h1 className='text-xl sm:text-2xl font-semibold text-foreground'>
            {dictionary['nav.profile'] || 'Profile'}
          </h1>
          <p className='text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2'>
            {dictionary['profile.view_account_info']}
          </p>
        </div>

        {/* Information Note */}
        <div className='mb-4 sm:mb-6 bg-secondary border border-primary/30 rounded-lg py-3 px-3 sm:py-3 sm:px-5'>
          <div className='flex items-start gap-3'>
            <div className='flex-shrink-0 p-1 sm:p-1.5 bg-secondary rounded-lg'>
              <UserIcon className='h-4 w-4 sm:h-5 sm:w-5 text-primary' />
            </div>
            <div className='flex-1 min-w-0'>
              <p className='text-xs sm:text-sm text-primary font-medium'>{dictionary['profile.managed_by_admin']}</p>
            </div>
          </div>
        </div>

        {/* Profile Overview */}
        <div className='bg-card border border-border rounded-xl mb-6 sm:mb-8'>
          {/* Profile Header */}
          <div className='px-4 py-4 sm:px-8 sm:py-8 border-b border-border'>
            <div className='flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6'>
              {/* Avatar */}
              <div className='w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary flex items-center justify-center flex-shrink-0'>
                <span className='text-xl sm:text-2xl font-semibold text-white'>
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>

              {/* User Info */}
              <div className='flex-1 text-center sm:text-left min-w-0'>
                <h2 className='text-xl sm:text-2xl font-semibold text-foreground truncate'>{user.name}</h2>
                <p className='text-sm sm:text-base text-muted-foreground mt-1 truncate'>{user.email}</p>
                <div className='flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-3 mt-3'>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium',
                      roleColors.bg,
                      roleColors.text
                    )}
                  >
                    <Shield className='h-3 w-3 sm:h-3.5 sm:w-3.5' />
                    {user?.assignedRole?.name
                      ? user.assignedRole.name
                      : formatRole(user.role)}
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium',
                      user.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    )}
                  >
                    {user.status === 'ACTIVE' ? (
                      <CheckCircle className='h-3 w-3 sm:h-3.5 sm:w-3.5' />
                    ) : (
                      <XCircle className='h-3 w-3 sm:h-3.5 sm:w-3.5' />
                    )}
                    {user.status === 'ACTIVE' ? dictionary['common.active'] : dictionary['common.inactive']}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className='px-4 py-4 sm:px-8 sm:py-6'>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6'>
            

              {/* Email */}
              <div className='flex items-start gap-2 sm:gap-3'>
                <div className='p-1.5 sm:p-2 bg-purple-50 rounded-lg flex-shrink-0'>
                  <Mail className='h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-600' />
                </div>
                <div className='min-w-0 flex-1'>
                  <p className='text-xs sm:text-sm text-muted-foreground'>{dictionary['profile.email_address']}</p>
                  <p className='text-xs sm:text-sm font-medium text-foreground mt-1 truncate'>{user.email}</p>
                </div>
              </div>

              {/* Account Created */}
              <div className='flex items-start gap-2 sm:gap-3'>
                <div className='p-1.5 sm:p-2 bg-green-50 rounded-lg flex-shrink-0'>
                  <Calendar className='h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600' />
                </div>
                <div className='min-w-0 flex-1'>
                  <p className='text-xs sm:text-sm text-muted-foreground'>{dictionary['profile.member_since']}</p>
                  <p className='text-xs sm:text-sm font-medium text-foreground mt-1'>
                    {formattedDate || dictionary['profile.na']}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div className='bg-card border border-border rounded-xl p-4 sm:p-6 mb-6 sm:mb-8'>
          <div className='flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6'>
            <div className='p-1.5 sm:p-2 bg-secondary rounded-lg flex-shrink-0'>
              <MapPin className='h-4 w-4 sm:h-5 sm:w-5 text-primary' />
            </div>
            <div className='min-w-0 flex-1'>
              <h3 className='text-base sm:text-lg font-medium text-foreground'>
                {dictionary['profile.location_information']}
              </h3>
              <p className='text-xs sm:text-sm text-muted-foreground mt-1'>
                {dictionary['profile.assigned_locations_hierarchy']}
              </p>
            </div>
          </div>

          {locationLoading ? (
            <div className='flex items-center justify-center py-8'>
              <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-primary'></div>
            </div>
          ) : (
            <div className='space-y-4 sm:space-y-6'>
              {/* Location Hierarchy */}
              <div>
                <p className='text-xs sm:text-sm font-medium text-muted-foreground mb-2 sm:mb-3'>
                  {dictionary['profile.location_hierarchy']}
                </p>
                <div className='bg-muted/30 px-3 py-2 sm:px-4 sm:py-3 rounded-lg border overflow-x-auto'>
                  <p className='text-sm sm:text-base text-foreground whitespace-nowrap'>{formatLocationHierarchy()}</p>
                </div>
              </div>

              {/* Individual Location Levels */}
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4'>
                {locationHierarchy?.provinceName && (
                  <div className='bg-muted/20 p-3 sm:p-4 rounded-lg border'>
                    <p className='text-xs text-muted-foreground mb-1'>{dictionary['form.province']}</p>
                    <p className='text-sm font-medium text-foreground truncate'>{locationHierarchy.provinceName}</p>
                  </div>
                )}
                {locationHierarchy?.districtName && (
                  <div className='bg-muted/20 p-3 sm:p-4 rounded-lg border'>
                    <p className='text-xs text-muted-foreground mb-1'>{dictionary['form.district']}</p>
                    <p className='text-sm font-medium text-foreground truncate'>{locationHierarchy.districtName}</p>
                  </div>
                )}
                {locationHierarchy?.sectorName && (
                  <div className='bg-muted/20 p-3 sm:p-4 rounded-lg border'>
                    <p className='text-xs text-muted-foreground mb-1'>{dictionary['form.sector']}</p>
                    <p className='text-sm font-medium text-foreground truncate'>{locationHierarchy.sectorName}</p>
                  </div>
                )}
                {locationHierarchy?.cellName && (
                  <div className='bg-muted/20 p-3 sm:p-4 rounded-lg border'>
                    <p className='text-xs text-muted-foreground mb-1'>{dictionary['form.cell']}</p>
                    <p className='text-sm font-medium text-foreground truncate'>{locationHierarchy.cellName}</p>
                  </div>
                )}
                {locationHierarchy?.villageName && (
                  <div className='bg-muted/20 p-3 sm:p-4 rounded-lg border'>
                    <p className='text-xs text-muted-foreground mb-1'>{dictionary['form.village']}</p>
                    <p className='text-sm font-medium text-foreground truncate'>{locationHierarchy.villageName}</p>
                  </div>
                )}
                {locationHierarchy?.assignedLocationName && (
                  <div className='bg-secondary p-3 sm:p-4 rounded-lg border border-primary/30'>
                    <p className='text-xs text-primary mb-1 font-medium'>{dictionary['profile.assigned_location']}</p>
                    <p className='text-sm font-semibold text-primary truncate'>
                      {locationHierarchy.assignedLocationName}
                    </p>
                    <p className='text-xs text-primary/70 mt-1'>
                      {dictionary['profile.type']} {locationHierarchy.assignedLocationType}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Assigned Locations (if multiple) */}
        {assignedLocations?.data?.locations && assignedLocations.data.locations.length > 0 && (
          <div className='bg-card border border-border rounded-xl p-4 sm:p-6'>
            <div className='flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6'>
              <div className='p-1.5 sm:p-2 bg-green-50 rounded-lg flex-shrink-0'>
                <MapPin className='h-4 w-4 sm:h-5 sm:w-5 text-green-600' />
              </div>
              <div className='min-w-0 flex-1'>
                <h3 className='text-base sm:text-lg font-medium text-foreground'>
                  {dictionary['profile.all_assigned_locations']}
                </h3>
                <p className='text-xs sm:text-sm text-muted-foreground mt-1'>
                  {dictionary['profile.locations_administration']}
                </p>
              </div>
            </div>

            {locationsLoading ? (
              <div className='flex items-center justify-center py-8'>
                <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-green-600'></div>
              </div>
            ) : (
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4'>
                {assignedLocations.data.locations.map((location: any) => (
                  <div
                    key={location.geoLocation.id}
                    className='bg-muted/20 p-3 sm:p-4 rounded-lg border border-border hover:border-green-300 transition-colors'
                  >
                    <p className='text-sm font-medium text-foreground truncate'>{location.geoLocation.name}</p>
                    <p className='text-xs text-muted-foreground mt-1'>{location.geoLocation.locationType}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Facilities you can access */}
        <div className='bg-card border border-border rounded-xl p-4 sm:p-6 mb-6 sm:mb-8'>
          <div className='flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6'>
            <div className='p-1.5 sm:p-2 bg-green-50 rounded-lg flex-shrink-0'>
              <BuildingIcon className='h-4 w-4 sm:h-5 sm:w-5 text-green-600' />
            </div>
            <div className='min-w-0 flex-1'>
              <h3 className='text-base sm:text-lg font-medium text-foreground'>
                {dictionary['profile.facilities_you_can_access'] || 'Facilities that you can access'}
              </h3>
              <p className='text-xs sm:text-sm text-muted-foreground mt-1'>
                {dictionary['profile.facility_types_by_role'] || 'Facility types you can view and assess by role'}
              </p>
            </div>
          </div>

          {user.roles?.length ? (
            <div className='space-y-6'>
              {user.roles.map((role: { id: string; name?: string; role?: string; facilityTypes?: { id: string; name: string }[] }) => (
                <div key={role.id} className='space-y-3'>
                  {(role.name ?? role.role) && (
                    <p className='text-xs sm:text-sm font-medium text-muted-foreground'>
                      {formatRole((role.name ?? role.role) as string)}
                    </p>
                  )}
                  <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4'>
                    {role.facilityTypes?.map((facilityType: { id: string; name: string }) => (
                      <div
                        key={facilityType.id}
                        className='bg-muted/20 p-1 sm:p-2 rounded-lg border border-border hover:border-green-300 transition-colors'
                      >
                        <p className='text-sm font-medium text-foreground truncate'>{facilityType.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='rounded-lg border border-dashed border-border bg-muted/10 py-8 px-4 text-center'>
              <p className='text-sm text-muted-foreground'>
                {dictionary['profile.no_facility_types'] || 'No facility types assigned yet.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
